import React from 'react';

/**
 * Shared presentational component for displaying Torah readings (leyning)
 * and Haftarah information.
 *
 * @param {object}  props
 * @param {object}  props.leyning          - Leyning data from the halachic engine (can be null)
 * @param {'summary'|'full'} props.mode    - 'summary' shows book + range; 'full' shows all aliyot
 * @param {boolean} props.showHaftarah     - Whether to display haftarah (default true)
 * @param {boolean} props.showMaftir       - Whether to display maftir (default false)
 * @param {boolean} props.compact          - Compact layout for smaller widgets (default false)
 * @param {object}  props.style            - Pass-through container style
 * @param {string}  props.className        - Pass-through className
 */
const LeyningDisplay = ({
  leyning,
  mode = 'summary',
  showHaftarah = true,
  showMaftir = false,
  compact = false,
  style,
  className,
}) => {
  if (!leyning) return null;

  const torahAliyot = leyning.torah || [];
  const haftaraText = leyning.haftaraHe || leyning.haftara || null;
  const maftir = leyning.maftir;

  // Build Torah summary: first aliyah book + begin  to  last aliyah book + end
  const torahSummary = (() => {
    if (!torahAliyot.length) return null;
    const first = torahAliyot[0];
    const last = torahAliyot[torahAliyot.length - 1];
    const fBegin = first.beginHe || first.begin;
    const lEnd = last.endHe || last.end;
    if (first.bookHe === last.bookHe) {
      return `${first.bookHe} ${fBegin} - ${lEnd}`;
    }
    return `${first.bookHe} ${fBegin} - ${last.bookHe} ${lEnd}`;
  })();

  const fontSize = compact ? '14px' : '17px';
  const labelSize = compact ? '12px' : '14px';
  const aliyahSize = compact ? '13px' : '15px';
  const gap = compact ? '6px' : '10px';

  return (
    <div
      className={className}
      style={{
        direction: 'rtl',
        textAlign: 'right',
        display: 'flex',
        flexDirection: 'column',
        gap,
        ...style,
      }}
    >
      {/* ── Torah ─────────────────────────────────── */}
      {mode === 'summary' && torahSummary && (
        <div style={S.row}>
          <span style={{ ...S.label, fontSize: labelSize }}>קריאת התורה</span>
          <span style={{ ...S.value, fontSize }}>{torahSummary}</span>
        </div>
      )}

      {mode === 'full' && torahAliyot.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{ ...S.label, fontSize: labelSize, marginBottom: '4px' }}>
            קריאת התורה
          </span>
          {torahAliyot.map((a) => (
            <div key={a.num} style={{ ...S.aliyahRow, fontSize: aliyahSize }}>
              <span style={S.aliyahNum}>{hebrewOrdinal(a.num)}</span>
              <span style={S.aliyahRef}>
                {a.bookHe} {a.beginHe || a.begin} - {a.endHe || a.end}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ── Maftir ────────────────────────────────── */}
      {showMaftir && maftir && (
        <div style={S.row}>
          <span style={{ ...S.label, fontSize: labelSize }}>מפטיר</span>
          <span style={{ ...S.value, fontSize }}>
            {maftir.bookHe} {maftir.beginHe || maftir.begin} - {maftir.endHe || maftir.end}
          </span>
        </div>
      )}

      {/* ── Haftarah ──────────────────────────────── */}
      {showHaftarah && haftaraText && (
        <div style={S.row}>
          <span style={{ ...S.label, fontSize: labelSize }}>הפטרה</span>
          <span style={{ ...S.value, fontSize }}>{haftaraText}</span>
          {leyning.reason && (
            <span style={S.reason}>({leyning.reason})</span>
          )}
        </div>
      )}
    </div>
  );
};

/** Map 1-7 to Hebrew ordinals for aliyot display */
function hebrewOrdinal(n) {
  const MAP = {
    1: 'ראשון',
    2: 'שני',
    3: 'שלישי',
    4: 'רביעי',
    5: 'חמישי',
    6: 'שישי',
    7: 'שביעי',
  };
  return MAP[n] || `עליה ${n}`;
}

const S = {
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    gap: '6px',
  },
  label: {
    fontWeight: 700,
    color: '#1e3a5f',
    flexShrink: 0,
  },
  value: {
    color: '#334155',
    fontWeight: 500,
  },
  reason: {
    fontSize: '12px',
    color: '#94a3b8',
    fontWeight: 400,
  },
  aliyahRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
    paddingRight: '8px',
  },
  aliyahNum: {
    fontWeight: 700,
    color: '#64748b',
    minWidth: '50px',
    flexShrink: 0,
  },
  aliyahRef: {
    color: '#334155',
    fontWeight: 500,
  },
};

export default LeyningDisplay;
